import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {FinancialPositionService} from "../../services/financial-position.service";
import {FinancialPosition} from "../../models/financial-position";
import {FinancialPositionTemplateService} from "../../services/financial-position-template.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Timestamp} from "firebase/firestore";
import {DateService} from "../../../tandem-core/services/date.service";
import {DialogService, TandemDialogConfig} from "../../../tandem-core/services/dialog.service";
import {Router} from "@angular/router";
import {LocalStorageService} from "../../../tandem-core/services/local-storage.service";
import {TandemUser} from "../../../auth/models/tandem-user";
import {HeaderService} from "../../../tandem-core/services/header.service";
import { TandemDatePipe } from 'src/app/modules/tandem-core/directives/tandem-date.pipe';

@Component({
  selector: 'tandem-financial-position-form',
  templateUrl: './financial-position-form.component.html',
  styleUrls: ['./financial-position-form.component.scss']
})
export class FinancialPositionFormComponent implements OnInit, AfterViewInit {

  @Input() public financialPosition: FinancialPosition = this.financialPositionTemplateService.generateDefaultPosition();
  @Input() public mode: 'create' | 'edit' | 'view' = 'create';
  @Input() public cacheChanges = false;
  @Input() public user: TandemUser | null = null;

  public currentPageNumber: number = 1;
  public pageSubtitle: string = `Let's start with what you own`;


  public fpForm: FormGroup = new FormGroup<any>({});

  @Output() public submitted: EventEmitter<FinancialPosition> = new EventEmitter<FinancialPosition>();

  @ViewChild('positionDate', {static: false})
  public positionDate?: TemplateRef<any>;

  constructor(private dateService: DateService,
              private financialPositionService: FinancialPositionService,
              private financialPositionTemplateService: FinancialPositionTemplateService,
              private fb: FormBuilder,
              private dialogService: DialogService,
              private router: Router,
              private localStorageService: LocalStorageService,
              private datePipe: TandemDatePipe,
              private header: HeaderService) {
  }

  ngOnInit(): void {
    this.fpForm = this.fb.group({
      positionDate: [this.financialPosition.positionDate.toDate().toISOString().substring(0, 10), Validators.required]
    });

    this.fpForm.get('positionDate')?.valueChanges.subscribe(newVal => {
      this.financialPosition.positionDate = this.dateService.getAdjustedTimestampFromDate(new Date(newVal));
      this.financialPosition.title = this.datePipe.transform(this.financialPosition.positionDate.toDate());
    });
  }

  ngAfterViewInit(): void {
    if (this.positionDate) {
      this.header.setTemplateRef(this.positionDate);
    }
  }

  public getPageLabel(): string {
    switch (this.currentPageNumber) {
      case 1:
        this.pageSubtitle = `Let's start with what you own`;
        return '1 of 3: Assets';
      case 2:
        this.pageSubtitle = `Now let's look at what you owe`;
        return '2 of 3: Liabilities';
      case 3:
        this.pageSubtitle = `Let's see an overview of your financial status`;
        return '3 of 3: Summary';
      default: return 'Done';
    }
  }

  public getHoverHintForPage(): string {
    switch (this.currentPageNumber) {
      case 1:
        return this.financialPosition.assetDescription || '';
      case 2:
        return this.financialPosition.liabilityDescription || '';
      case 3:
        return `Take a look at your overall financial standing`
      default: return '';
    }
  }

  public setPage(pageNum: number): void {
    this.recalculateTotals();
    if (pageNum == 0) {
      if (this.currentPageNumber - pageNum == 1 && this.currentPageNumber == 1) {
        let modalConfig: TandemDialogConfig = {
          title: 'Do you want to save first?',
          type: 'confirm',
          content: `It looks like you're about to exit the ${this.getTrackingToolName('single')}, do you want to save your work before leaving the page?`,
          actions: [
            {
              text: 'Cancel',
              role: 'cancel',
              callback: () => {
                this.dialogService.closeModal2();
              }
            },
            {
              text: 'No Thanks',
              role: 'confirm',
              callback: () => {
                this.dialogService.closeModal2();
                this.localStorageService.removeItem('createFinancialPosition');
                if (this.mode !== 'create') {
                  this.router.navigate([`/financial-positions/${this.financialPosition.id}`]);
                } else {
                  this.router.navigate(["/financial-positions"]);
                }
              }
            },
            {
              text: 'Save and Exit',
              role: 'primary',
              callback: () => {
                this.dialogService.closeModal2();
                this.onSubmit();
              }
            }
          ]
        }
        this.dialogService.openModal2(modalConfig);
      } else {
        this.recalculateTotals();
        this.currentPageNumber = pageNum;
        this.getPageLabel();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } else {
      this.recalculateTotals();
      this.currentPageNumber = pageNum;
      this.getPageLabel();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  public onSubmit(): void {
    this.financialPosition.title = this.datePipe.transform(this.financialPosition.positionDate.toDate());
    this.submitted.emit(this.financialPosition);
  }

  public recalculateTotals() {
    let totalAssets = 0;
    let totalLiquidAssets = 0;
    let totalLiabilities = 0;
    let totalShortTermLiabilities = 0;
    let minimumMonthlyPayment = 0;
    this.financialPosition.assets.forEach(category => {
      category.assets.forEach(asset => {
        totalAssets += asset.value;
        // TODO: more accurate liquidity check
        if (asset.liquidityUnits === 'Instant' || (asset.liquidityQuantity && asset.liquidityQuantity <= 31 && asset.liquidityUnits === 'Days') || (asset.liquidityQuantity && asset.liquidityQuantity <= 4 && asset.liquidityUnits === 'Weeks') || (asset.liquidityQuantity && asset.liquidityQuantity <= 1 && asset.liquidityUnits === 'Months')) {
          totalLiquidAssets += asset.value;
        }
        // TODO: calculate average return
      })
    });
    this.financialPosition.liabilities.forEach(category => {
      category.liabilities.forEach(liability => {
        totalLiabilities += liability.value;
        // TODO: more accurate term check
        if (liability.termUnits === 'N/A' || (liability.termQuantity && liability.termQuantity <= 31 && liability.termUnits === 'Days')) {
          totalShortTermLiabilities += liability.value;
        }
        minimumMonthlyPayment += (liability?.minimumPayment || 0);
      });
    })

    this.financialPosition.totalAssets = totalAssets;
    this.financialPosition.liquidAssets = totalLiquidAssets;
    this.financialPosition.totalLiabilities = totalLiabilities;
    this.financialPosition.shortTermLiabilities = totalShortTermLiabilities;
    this.financialPosition.totalMinimumPayment = minimumMonthlyPayment;
    this.financialPosition.netWorth = totalAssets - totalLiabilities;

    let averageReturn = 0;
    let averageInterestRate = 0;

    this.financialPosition.assets.forEach(category => {
      category.assets.forEach(asset => averageReturn += (asset.value / totalAssets * asset.annualPercentageReturn));
    });
    this.financialPosition.averageReturn = averageReturn;

    this.financialPosition.liabilities.forEach(category => {
      category.liabilities.forEach(liability => averageInterestRate += (liability.value / totalLiabilities * liability.interestRate));
    });
    this.financialPosition.averageInterestRate = averageInterestRate;
    if (this.cacheChanges) {
      this.localStorageService.saveItem('createFinancialPosition', this.financialPosition);
    }
  }

  getTrackingToolName(variant: 'single' | 'plural') {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.fpName ? this.user.customTTMetadata.fpName : 'Financial Position';
    } else {
      return this.user?.customTTMetadata?.fpName ? `${this.user.customTTMetadata.fpName}` : 'Financial Positions';
    }
  }

  getTrackingToolDescription() {
    return !!this.user?.customTTMetadata?.fpName ? this.user.customTTMetadata.fpName : 'A summary of what you own, your assets, and what you owe, your liabilities at a given point in time'
  }


}
